import React, { useState } from 'react';
import { FaTrashAlt } from 'react-icons/fa';

import PartyHallServiceFields from './PartyHallServiceFields';
import {
  CAPACITY_RESTAURANT_SERVICE_TYPES,
  DINEIN_RESTAURANT_SERVICE_TYPES,
  PARTY_HALL_SERVICE_TYPE,
  RESTAURANT_SERVICE_LABELS,
  RESTAURANT_SERVICE_TYPES,
  emptyServiceForm,
  formatGuestCountRange,
  formatHallType,
  formatNoOfTables,
  validateServicePayload,
} from './restaurantServiceConstants';
import './css/restaurantServices.css';

const RestaurantServicesPicker = ({ selections, onChange, disabled = false }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState(() => emptyServiceForm());

  const updateType = (type, patch) => {
    onChange({
      ...selections,
      [type]: {
        ...(selections?.[type] || {}),
        ...patch,
      },
    });
  };

  const selectedTypes = RESTAURANT_SERVICE_TYPES.filter((type) => selections?.[type]?.selected);
  const availableTypes = RESTAURANT_SERVICE_TYPES.filter((type) => !selections?.[type]?.selected);
  const addFormIsCapacity = CAPACITY_RESTAURANT_SERVICE_TYPES.has(
    String(addForm.serviceType || '').toUpperCase()
  );
  const addFormIsDineIn = DINEIN_RESTAURANT_SERVICE_TYPES.has(
    String(addForm.serviceType || '').toUpperCase()
  );
  const addFormIsPartyHall =
    String(addForm.serviceType || '').toUpperCase() === PARTY_HALL_SERVICE_TYPE;

  const openAddForm = () => {
    setAddForm({
      ...emptyServiceForm(),
      serviceType: availableTypes[0] || '',
    });
    setShowAddForm(true);
  };

  const closeAddForm = () => {
    setShowAddForm(false);
    setAddForm(emptyServiceForm());
  };

  const handleAddService = () => {
    const serviceType = String(addForm.serviceType || '').trim().toUpperCase();
    if (!serviceType) return;

    const validationError = validateServicePayload(serviceType, addForm);
    if (validationError) {
      alert(validationError);
      return;
    }

    updateType(serviceType, {
      selected: true,
      status: addForm.status || 'ACTIVE',
      minGuestCount: addForm.minGuestCount,
      maxGuestCount: addForm.maxGuestCount,
      noOfTables: addForm.noOfTables,
      hallName: addForm.hallName,
      hallType: addForm.hallType,
      description: addForm.description,
      basePrice: addForm.basePrice,
      amenities: addForm.amenities,
      eventTypes: addForm.eventTypes,
    });
    closeAddForm();
  };

  const removeService = (type) => {
    updateType(type, {
      selected: false,
      status: 'ACTIVE',
      minGuestCount: '',
      maxGuestCount: '',
      noOfTables: '',
      hallName: '',
      hallType: '',
      description: '',
      basePrice: '',
      amenities: '',
      eventTypes: '',
    });
  };

  return (
    <div className="restaurant-services-add-picker">
      <div className="restaurant-services-add-header">
        <div>
          <h3 className="restaurant-services-add-title">Services</h3>
        </div>
        {availableTypes.length > 0 ? (
          <button
            type="button"
            className="restaurant-services-add-trigger"
            onClick={openAddForm}
            disabled={disabled || showAddForm}
          >
            + Add Service
          </button>
        ) : null}
      </div>

      {showAddForm ? (
        <div className="restaurant-services-form restaurant-services-add-form">
          <div className="restaurant-services-form-grid restaurant-services-add-form-grid">
            <label className="restaurant-services-picker-field">
              <span className="restaurant-services-picker-field-label">Service</span>
              <select
                value={addForm.serviceType}
                onChange={(e) =>
                  setAddForm((current) => ({ ...current, serviceType: e.target.value }))
                }
                disabled={disabled}
              >
                {availableTypes.map((type) => (
                  <option key={type} value={type}>
                    {RESTAURANT_SERVICE_LABELS[type] || type}
                  </option>
                ))}
              </select>
            </label>
            <label className="restaurant-services-picker-field">
              <span className="restaurant-services-picker-field-label">Status</span>
              <select
                value={addForm.status}
                onChange={(e) => setAddForm((current) => ({ ...current, status: e.target.value }))}
                disabled={disabled}
              >
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
              </select>
            </label>
            {addFormIsCapacity ? (
              <>
                <label className="restaurant-services-picker-field">
                  <span className="restaurant-services-picker-field-label">Min Guests</span>
                  <input
                    type="number"
                    min="1"
                    value={addForm.minGuestCount}
                    onChange={(e) =>
                      setAddForm((current) => ({ ...current, minGuestCount: e.target.value }))
                    }
                    disabled={disabled}
                    placeholder="Minimum guests"
                  />
                </label>
                <label className="restaurant-services-picker-field">
                  <span className="restaurant-services-picker-field-label">Max Guests</span>
                  <input
                    type="number"
                    min="1"
                    value={addForm.maxGuestCount}
                    onChange={(e) =>
                      setAddForm((current) => ({ ...current, maxGuestCount: e.target.value }))
                    }
                    disabled={disabled}
                    placeholder="Optional maximum"
                  />
                </label>
              </>
            ) : null}
            {addFormIsDineIn ? (
              <label className="restaurant-services-picker-field">
                <span className="restaurant-services-picker-field-label">No. of Tables</span>
                <input
                  type="number"
                  min="1"
                  value={addForm.noOfTables}
                  onChange={(e) =>
                    setAddForm((current) => ({ ...current, noOfTables: e.target.value }))
                  }
                  disabled={disabled}
                  placeholder="Enter number of tables"
                />
              </label>
            ) : null}
            {addFormIsPartyHall ? (
              <PartyHallServiceFields
                value={addForm}
                onChange={setAddForm}
                disabled={disabled}
              />
            ) : null}
          </div>
          <div className="restaurant-services-form-actions">
            <button
              type="button"
              className="restaurant-services-add-cancel"
              onClick={closeAddForm}
              disabled={disabled}
            >
              Cancel
            </button>
            <button
              type="button"
              className="restaurant-services-add-submit"
              onClick={handleAddService}
              disabled={disabled || !addForm.serviceType}
            >
              Save Service
            </button>
          </div>
        </div>
      ) : null}

      {selectedTypes.length > 0 ? (
        <div className="restaurant-services-add-list-wrap">
          <table className="restaurant-services-table restaurant-services-add-table">
            <thead>
              <tr>
                <th>Service</th>
                <th>Status</th>
                <th>Guests</th>
                <th>Tables</th>
                <th>Hall Type</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {selectedTypes.map((type) => {
                const row = selections[type] || {};
                return (
                  <tr key={type}>
                    <td>{RESTAURANT_SERVICE_LABELS[type] || type}</td>
                    <td>
                      <span
                        className={`restaurant-services-add-status restaurant-services-add-status--${String(
                          row.status || 'ACTIVE'
                        ).toLowerCase()}`}
                      >
                        {row.status || 'ACTIVE'}
                      </span>
                    </td>
                    <td>{formatGuestCountRange(row)}</td>
                    <td>{formatNoOfTables(row)}</td>
                    <td>{type === PARTY_HALL_SERVICE_TYPE ? formatHallType(row) : '—'}</td>
                    <td className="restaurant-services-add-actions-cell">
                      <button
                        type="button"
                        className="restaurant-services-add-remove"
                        onClick={() => removeService(type)}
                        disabled={disabled}
                        aria-label={`Remove ${RESTAURANT_SERVICE_LABELS[type] || type}`}
                      >
                        <FaTrashAlt aria-hidden="true" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="restaurant-services-add-empty">
          No services added yet. Use Add service to configure offerings for this restaurant.
        </p>
      )}
    </div>
  );
};

export default RestaurantServicesPicker;
